import {
  createContext,
  useContext,
  useEffect,
  useEffectEvent,
  useMemo,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/useAuthStore';
import { useGuestStore } from '@/stores/useGuestStore';
import { usePreferenceStore } from '@/stores/usePreferenceStore';

type EntryScope = { entry: string; loads: Set<string> };
const EntryLoads = createContext<EntryScope | undefined>(undefined);

/** One initial metadata read per navigation. Concealment never renews a private window. */
export function PageEntryLoadProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const auth = useAuthStore((s) => s.sessionEpoch);
  const guest = useGuestStore((s) => s.sessionEpoch);
  const display = usePreferenceStore((s) => `${s.locale}:${s.currency}`);
  const scope = useMemo(
    () => ({
      entry: `${location.key}:${auth}:${guest}:${display}`,
      loads: new Set<string>(),
    }),
    [location.key, auth, guest, display],
  );
  return <EntryLoads value={scope}>{children}</EntryLoads>;
}

/** Switching a visible tab grants an entry; reopening hidden content does not. */
export function PageEntryLoadScope({
  entry,
  children,
}: {
  entry: string;
  children: ReactNode;
}) {
  const parent = useContext(EntryLoads);
  const scope = useMemo(
    () =>
      parent
        ? { entry: `${parent.entry}:${entry}`, loads: new Set<string>() }
        : undefined,
    [parent, entry],
  );
  return <EntryLoads value={scope}>{children}</EntryLoads>;
}

export function usePageEntryLoad(
  key: string,
  load: () => void,
  enabled = true,
) {
  const scope = useContext(EntryLoads);
  const initialLoad = useEffectEvent(load);
  useEffect(() => {
    let mounted = true;
    queueMicrotask(() => {
      if (
        !mounted ||
        !enabled ||
        !scope ||
        scope.loads.has(key) ||
        document.visibilityState === 'hidden' ||
        navigator.onLine === false
      )
        return;
      // The existing private reader has armed by now. StrictMode discards its
      // first setup before this microtask, preventing duplicate HTTP requests.
      scope.loads.add(key);
      initialLoad();
    });
    return () => {
      mounted = false;
    };
  }, [scope, key, enabled]);
}
